/**
 * Apply existing customer credit (tracker item 55): the payment, invoice and
 * customer page sections. Credit is an ACTIVE payment's unapplied amount
 * (overpayment, or released by cancelling an invoice). Applying it posts no
 * accounting entry; the server re-checks every amount under row locks.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { createAPIClient } from '../../api';
import * as types from '../../api/types';
import { getErrorMessage } from '../../utils/apiError';
import { inputClass, labelClass, primaryButtonClass } from '../../components/formStyles';
import { showToast } from '../../components/Toast';
import { invoiceDate, invoiceMoney } from '../../pages/invoiceUi';
import {
  applicationSourceLabel,
  creditAmountError,
  creditPaymentLabel,
  defaultCreditAmount,
  invoiceApplicationRows,
  InvoiceApplicationRow,
  toCents,
} from './customerCreditUi';

const sectionClass = 'bg-white rounded-lg shadow p-4 sm:p-5 mb-5';
const OPEN_STATUSES: string[] = [types.InvoiceStatus.POSTED, types.InvoiceStatus.PARTIAL];

/** Shared amount + submit row. */
function AmountForm({ id, amount, setAmount, busy, error, submitLabel, onSubmit, children }: {
  id: string;
  amount: string;
  setAmount: (value: string) => void;
  busy: boolean;
  error: string | null;
  submitLabel: string;
  onSubmit: () => void;
  children: React.ReactNode;
}) {
  return <form className="flex flex-col sm:flex-row sm:flex-wrap sm:items-end gap-3" onSubmit={(event) => { event.preventDefault(); onSubmit(); }}>
    {children}
    <div className="sm:w-40"><label className={labelClass} htmlFor={`${id}-amount`}>Amount</label><input id={`${id}-amount`} inputMode="decimal" value={amount} onChange={(event) => setAmount(event.target.value)} className={inputClass} /></div>
    <button type="submit" disabled={busy} className={primaryButtonClass}>{submitLabel}</button>
    {error && <p role="alert" className="text-sm text-red-700 w-full">{error}</p>}
  </form>;
}

/**
 * Payment detail: apply this payment's remaining credit to an open invoice of
 * the same billing customer.
 */
export function PaymentApplyCreditPanel({ payment, onApplied }: { payment: types.Payment; onApplied: (result: types.ApplyCreditResult) => void }) {
  const api = useMemo(() => createAPIClient(), []);
  const [targets, setTargets] = useState<types.CreditTargets | null>(null);
  const [invoiceId, setInvoiceId] = useState('');
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const result = await api.getCreditTargets(payment.id);
    setTargets(result);
    const first = result.invoices[0];
    setInvoiceId(first?.id ?? '');
    setAmount(first ? defaultCreditAmount(result.unappliedAmount, first.remainingBalance) : '');
  }, [api, payment.id]);

  useEffect(() => {
    load().catch((cause) => setError(getErrorMessage(cause, 'Could not load open invoices')));
    // Reload whenever the payment changes (apply, unapply, void)
  }, [load, payment]);

  if (!targets || !targets.canApply) return error ? <section className={sectionClass}><p role="alert" className="text-sm text-red-700">{error}</p></section> : null;
  const selected = targets.invoices.find((invoice) => invoice.id === invoiceId);

  async function apply() {
    if (!selected || !targets) return;
    const problem = creditAmountError(amount, targets.unappliedAmount, selected.remainingBalance);
    if (problem) { setError(problem); return; }
    setBusy(true); setError(null);
    try {
      const result = await api.applyCredit(payment.id, { invoiceId: selected.id, amount: amount.trim() });
      showToast('success', `Applied ${invoiceMoney(result.application.amountApplied)} to invoice #${result.invoice.invoiceNumber}. Remaining credit ${invoiceMoney(result.payment.unappliedAmount)}.`);
      onApplied(result);
    } catch (cause) {
      setError(getErrorMessage(cause, 'Could not apply the credit'));
      await load().catch(() => undefined);
    } finally { setBusy(false); }
  }

  return <section className={sectionClass} aria-label="Apply credit">
    <h2 className="font-semibold text-lg mb-1">Apply credit</h2>
    <p className="text-sm text-gray-600 mb-4">{invoiceMoney(targets.unappliedAmount)} of this payment is not applied to any invoice. Apply it to an open invoice for {payment.billingCustomer?.name || 'this customer'}. No new accounting entry is posted: the payment already reduced the customer's receivable.</p>
    {targets.invoices.length === 0
      ? <p className="text-sm text-gray-600">This customer has no open invoices. The credit stays on the payment.</p>
      : <AmountForm id="payment-credit" amount={amount} setAmount={setAmount} busy={busy} error={error} submitLabel="Apply credit" onSubmit={() => void apply()}>
        <div className="sm:flex-1 min-w-0"><label className={labelClass} htmlFor="payment-credit-invoice">Invoice</label>
          <select id="payment-credit-invoice" className={inputClass} value={invoiceId} onChange={(event) => {
            setInvoiceId(event.target.value);
            const next = targets.invoices.find((invoice) => invoice.id === event.target.value);
            if (next) setAmount(defaultCreditAmount(targets.unappliedAmount, next.remainingBalance));
          }}>
            {targets.invoices.map((invoice) => <option key={invoice.id} value={invoice.id}>#{invoice.invoiceNumber} ({invoiceDate(invoice.invoiceDate)}): {invoiceMoney(invoice.remainingBalance)} due</option>)}
          </select>
        </div>
      </AmountForm>}
  </section>;
}

/**
 * Invoice detail: every ACTIVE payment and credit applied to the invoice, the
 * balance due, and (office roles, open invoice) applying the customer's credit.
 */
export function InvoiceCreditPanel({ invoice, canManage, onChanged }: { invoice: types.Invoice; canManage: boolean; onChanged: () => void }) {
  const api = useMemo(() => createAPIClient(), []);
  const [rows, setRows] = useState<InvoiceApplicationRow[] | null>(null);
  const [credits, setCredits] = useState<types.CustomerCredits | null>(null);
  const [paymentId, setPaymentId] = useState('');
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const open = OPEN_STATUSES.includes(invoice.status);

  const load = useCallback(async () => {
    const payments: types.Payment[] = [];
    let page = 1;
    let pages = 1;
    do {
      const result = await api.listPayments(page, 100, { invoiceId: invoice.id, status: 'ACTIVE' });
      payments.push(...result.data);
      pages = result.pagination.pages;
      page += 1;
    } while (page <= pages);
    const applied = invoiceApplicationRows(payments, invoice.id);
    setRows(applied);
    if (canManage && open) {
      const customerCredits = await api.getCustomerCredits(invoice.billingCustomerId);
      setCredits(customerCredits);
      const balance = (toCents(invoice.total) - applied.reduce((sum, row) => sum + toCents(row.amount), 0)) / 100;
      const first = customerCredits.payments.find((payment) => payment.applicable);
      setPaymentId(first?.paymentId ?? '');
      setAmount(first ? defaultCreditAmount(first.unappliedAmount, balance.toFixed(2)) : '');
    } else {
      setCredits(null);
    }
  }, [api, invoice.id, invoice.billingCustomerId, invoice.total, canManage, open]);

  useEffect(() => {
    load().catch((cause) => setError(getErrorMessage(cause, 'Could not load payments for this invoice')));
  }, [load, invoice]);

  if (invoice.status === types.InvoiceStatus.DRAFT) return null;
  const paid = (rows ?? []).reduce((sum, row) => sum + toCents(row.amount), 0);
  const balance = (Math.max(0, toCents(invoice.total) - paid) / 100).toFixed(2);
  const applicable = (credits?.payments ?? []).filter((payment) => payment.applicable);
  const selected = applicable.find((payment) => payment.paymentId === paymentId);

  async function apply() {
    if (!selected) return;
    const problem = creditAmountError(amount, selected.unappliedAmount, balance);
    if (problem) { setError(problem); return; }
    setBusy(true); setError(null);
    try {
      const result = await api.applyCredit(selected.paymentId, { invoiceId: invoice.id, amount: amount.trim() });
      showToast('success', `Applied ${invoiceMoney(result.application.amountApplied)} of credit. Balance due ${invoiceMoney(result.invoice.remainingBalance)}.`);
      onChanged();
    } catch (cause) {
      setError(getErrorMessage(cause, 'Could not apply the credit'));
      await load().catch(() => undefined);
    } finally { setBusy(false); }
  }

  return <section className={sectionClass} aria-label="Payments and credit">
    <h2 className="font-semibold text-lg mb-4">Payments and credit</h2>
    {rows === null ? <p className="text-sm text-gray-600">{error ?? 'Loading payments…'}</p> : <>
      {rows.length === 0 ? <p className="text-sm text-gray-600">Nothing has been applied to this invoice.</p>
        : <div className="overflow-x-auto"><table className="responsive-table min-w-full text-sm"><thead><tr className="border-b">{['Date', 'Payment', 'Type', 'Amount'].map((heading) => <th key={heading} className="text-left px-3 py-2">{heading}</th>)}</tr></thead>
          <tbody>{rows.map((row) => <tr key={row.applicationId} className="border-b" data-testid="invoice-application"><td data-label="Date" className="px-3 py-3">{invoiceDate(row.date)}</td><td data-label="Payment" data-primary className="px-3 py-3"><Link className="text-blue-700 hover:underline" to={`/payments/${row.paymentId}`}>{row.paymentReference}</Link></td><td data-label="Type" className="px-3 py-3">{applicationSourceLabel(row)}</td><td data-label="Amount" className="px-3 py-3">{invoiceMoney(row.amount)}</td></tr>)}</tbody></table></div>}
      {invoice.status !== types.InvoiceStatus.VOID && <p className="mt-3 text-sm font-semibold">Balance due: {invoiceMoney(balance)}</p>}
      {error && !selected && <p role="alert" className="mt-3 text-sm text-red-700">{error}</p>}
      {credits && Number(credits.totalUnapplied) > 0 && open && toCents(balance) > 0 && <div className="mt-5 border-t pt-4">
        <h3 className="font-semibold mb-1">Apply customer credit</h3>
        <p className="text-sm text-gray-600 mb-3">{invoice.billingCustomer?.name || 'This customer'} has {invoiceMoney(credits.totalUnapplied)} of unapplied credit. No new accounting entry is posted.</p>
        {applicable.length === 0 ? <p className="text-sm text-gray-600">The credit belongs to a card payment being refunded and cannot be applied.</p>
          : <AmountForm id="invoice-credit" amount={amount} setAmount={setAmount} busy={busy} error={error} submitLabel="Apply credit" onSubmit={() => void apply()}>
            <div className="sm:flex-1 min-w-0"><label className={labelClass} htmlFor="invoice-credit-payment">From payment</label>
              <select id="invoice-credit-payment" className={inputClass} value={paymentId} onChange={(event) => {
                setPaymentId(event.target.value);
                const next = applicable.find((payment) => payment.paymentId === event.target.value);
                if (next) setAmount(defaultCreditAmount(next.unappliedAmount, balance));
              }}>
                {applicable.map((payment) => <option key={payment.paymentId} value={payment.paymentId}>{creditPaymentLabel(payment)}</option>)}
              </select>
            </div>
          </AmountForm>}
      </div>}
    </>}
  </section>;
}

/** Customer detail: the customer's unapplied credit and the payments carrying it. */
export function CustomerCreditCard({ customerId }: { customerId: string }) {
  const api = useMemo(() => createAPIClient(), []);
  const [credits, setCredits] = useState<types.CustomerCredits | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    // Through a promise, so any failure (even a synchronous one) lands in the card, never in the page
    Promise.resolve().then(() => api.getCustomerCredits(customerId))
      .then((result) => { if (active) setCredits(result); })
      .catch((cause) => { if (active) setError(getErrorMessage(cause, 'Could not load customer credit')); });
    return () => { active = false; };
  }, [api, customerId]);

  return <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6" aria-label="Customer credit">
    <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-4">Customer credit</h2>
    {error ? <p role="alert" className="text-sm text-red-700">{error}</p>
      : !credits ? <p className="text-sm text-gray-500">Loading…</p>
        : credits.payments.length === 0 ? <p className="text-sm text-gray-500">No unapplied credit.</p>
          : <>
            <p className="text-sm mb-3"><span className="font-semibold">{invoiceMoney(credits.totalUnapplied)}</span> of payments is not applied to any invoice. Open a payment to apply its credit, or apply it from an open invoice.</p>
            <ul className="divide-y text-sm">{credits.payments.map((payment) => <li key={payment.paymentId} className="py-2 flex flex-wrap justify-between gap-2" data-testid="customer-credit-payment">
              <Link className="text-blue-700 hover:underline" to={`/payments/${payment.paymentId}`}>{payment.paymentMethod.replace('_', ' ')}{payment.referenceNumber ? ` ${payment.referenceNumber}` : ''} · {invoiceDate(payment.paymentDate)}</Link>
              <span>{invoiceMoney(payment.unappliedAmount)} of {invoiceMoney(payment.amount)}{!payment.applicable && <span className="text-amber-800"> (refund in progress)</span>}</span>
            </li>)}</ul>
          </>}
  </section>;
}
